import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, IconButton, Paper, Popover,
  Avatar, useMediaQuery, Tooltip, BottomNavigation, BottomNavigationAction,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, CircularProgress,
  Badge,
} from '@mui/material';
import {
  Landscape,
  Cloud,
  Logout,
  Menu as MenuIcon,
  CalendarMonth,
  Settings,
  People,
  Insights,
  MenuBook,
  Analytics as AnalyticsIcon,
  NotificationsActive,
  HelpOutline,
  Person,
  TrendingUp,
  MoveToInbox,
  Router as RouterIcon,
  Tune as TuneIcon,
  History as HistoryIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageToggle from '../LanguageToggle';
import WeatherWidget from '../dashboard/WeatherWidget';
import NotificationBell from './NotificationBell';
import InstallBanner from '../ui/InstallBanner';
import TourBanner from '../ui/TourBanner';
import { BRAND, TEXT, SURFACE } from '../../theme/colors';

const ICON_RAIL_WIDTH = 56;
const FULL_DRAWER_WIDTH = 220;

// Farmer navigation — what a farmer sees in the sidebar / bottom nav.
// Slimmed 2026-10-09 to the phone's shape, which the client found easier: five
// destinations in the sidebar (Material: 3-5 top-level destinations), the rest
// (`more`) in the profile menu, as on the phone. Alerts and weather also have
// their top-bar bell and chip; field data and hardware are a press away.
// /sensors, /users are admin-only and never appear here.
// Audit step 4 (George 16/10/2026): /fields merged into home, which is the
// list of fields; the field data charts moved into the field page; the journal,
// with no use case in the brief, left the main destinations for the profile
// menu and its place went to the alerts the email and the bell link to.
const FARMER_NAV = [
  { path: '/', label: 'myFields', icon: <Landscape /> },
  { path: '/schedules', label: 'weeklySchedules', icon: <CalendarMonth /> },
  { path: '/reports', label: 'reportsNavLabel', icon: <DescriptionIcon /> },
  { path: '/alerts', label: 'alerts', icon: <NotificationsActive /> },
  { path: '/devices', label: 'hardwareNavLabel', icon: <RouterIcon />, more: true },
  { path: '/weather', label: 'weather', icon: <Cloud />, more: true },
  { path: '/journal', label: 'fieldJournal', icon: <MenuBook />, more: true },
];
const FARMER_MAIN = FARMER_NAV.filter(i => !i.more);
const FARMER_MORE = FARMER_NAV.filter(i => i.more);

// Admin navigation — system administration only.
// Alerts, Schedule, Farm Journal are farmer features and do not belong here.
// Admin monitors all users' fields via the map and system analytics.
// Slimmed 2026-10-08 like the farmer view: an item with `ent` shows only to a
// user who holds that entitlement. The operator tools are staff-only on the
// server (myproject/entitlements.py STAFF_ENTITLEMENTS), so the client, an
// Admin-group account, sees Map, Fields, Users, Hardware, Reports and the soil
// tables he authored.
const ADMIN_NAV = [
  { path: '/', label: 'myFields', icon: <Landscape /> },
  { path: '/users', label: 'userManagement', icon: <People /> },
  { path: '/devices', label: 'hardwareNavLabel', icon: <RouterIcon /> },
  { path: '/reports', label: 'reportsNavLabel', icon: <DescriptionIcon /> },
  { path: '/admin-analytics', label: 'systemAnalytics', icon: <TrendingUp />, ent: 'admin_analytics' },
  // What the engine recommended against the runs made, from /analytics (step 4 M8).
  // selfGuarded: the page sends a grower on itself (an old ?field= link opens
  // that field's charts), so the layout must not send him home first.
  { path: '/analytics', label: 'recViewNavLabel', icon: <Insights />, ent: 'admin_analytics', selfGuarded: true },
  // The client authored these tables and edits them (2026-07-27); not staff-only.
  { path: '/admin/soil-profiles', label: 'soilProfiles', icon: <TuneIcon /> },
  { path: '/admin/history', label: 'historyNav', icon: <HistoryIcon /> },
  { path: '/messages', label: 'supportInbox', icon: <MoveToInbox />, badgeKey: 'messages', ent: 'support_inbox' },
  { path: '/dss-demo', label: 'dssDemo', icon: <AnalyticsIcon />, ent: 'parameter_playground' },
];

// Section label translations
const SECTION_LABELS = {
  operations: { en: 'Operations', el: '\u039B\u03B5\u03B9\u03C4\u03BF\u03C5\u03C1\u03B3\u03AF\u03B5\u03C2' },
  monitoring: { en: 'Monitoring', el: '\u03A0\u03B1\u03C1\u03B1\u03BA\u03BF\u03BB\u03BF\u03CD\u03B8\u03B7\u03C3\u03B7' },
  preferences: { en: 'Preferences', el: '\u03A1\u03C5\u03B8\u03BC\u03AF\u03C3\u03B5\u03B9\u03C2' },
  admin: { en: 'Admin', el: '\u0394\u03B9\u03B1\u03C7\u03B5\u03AF\u03C1\u03B9\u03C3\u03B7' },
};

const PageLayout = ({ children, title, topBarRight }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, hasAdminRole, viewMode, setViewMode, hasEntitlement } = useAuth();
  const { t, language } = useLanguage();
  const isSmallScreen = useMediaQuery('(max-width:960px)');
  const isMapView = title === 'map';
  // Pages where the nav sidebar auto-collapses to icon-only to give more working space
  const isWideContentPage = ['map', 'weeklySchedules', 'dssDemo'].includes(title);

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  // createMenuAnchor removed — FAB was the only trigger, now deleted.
  // "Add field" lives in FieldListSidebar header; "Add note" in map long-press.
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpType, setHelpType] = useState('question');
  const [helpMsg, setHelpMsg] = useState('');
  const [helpSending, setHelpSending] = useState(false);
  const [helpSent, setHelpSent] = useState(false);

  const HELP_TYPES = [
    { value: 'question',  labelKey: 'helpTypeQuestion',  label: 'General Question', emoji: '❓' },
    { value: 'bug',       labelKey: 'helpTypeBug',       label: 'Bug Report',       emoji: '🐛' },
    { value: 'feature',   labelKey: 'helpTypeFeature',   label: 'Feature Request',  emoji: '💡' },
    { value: 'data',      labelKey: 'helpTypeData',      label: 'Data Issue',       emoji: '📊' },
  ];

  const sendHelp = async () => {
    setHelpSending(true);
    const typeLabel = HELP_TYPES.find(h => h.value === helpType)?.label || helpType;
    try {
      await axios.post(API_ENDPOINTS.SUPPORT_CREATE, {
        subject: `[${typeLabel}] ${user?.username || ''}`,
        body: helpMsg,
        message_type: helpType,
      });
      setHelpSent(true);
      setTimeout(() => {
        setHelpOpen(false);
        setHelpMsg('');
        setHelpType('question');
        setHelpSent(false);
      }, 1800);
    } catch {
      setHelpSent(false);
    } finally {
      setHelpSending(false);
    }
  };

  // The simple (farmer) view is the default even for an admin account
  // (REQUIREMENTS C2); the switch below opens the admin tools. The mode lives
  // in AuthContext, so isAdmin already honours it on every screen.
  const adminViewMode = viewMode;
  const effectiveAdmin = isAdmin;

  // Unread support messages badge — polls every 60s for whoever may open the
  // support inbox (staff), not every admin account.
  const [unreadMessages, setUnreadMessages] = useState(0);
  const canSeeInbox = effectiveAdmin && !!hasEntitlement?.('support_inbox');
  useEffect(() => {
    if (!canSeeInbox) return;
    const fetchUnread = () => {
      axios.get(API_ENDPOINTS.SUPPORT_UNREAD_COUNT)
        .then(r => setUnreadMessages(r.data?.unread_count || 0))
        .catch(() => { /* non-fatal — badge missing is acceptable */ });
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 60000);
    return () => clearInterval(iv);
  }, [canSeeInbox]);

  const toggleViewMode = () => setViewMode(adminViewMode === 'farmer' ? 'admin' : 'farmer');

  // When an admin switches to "farmer view" they should not remain on an
  // admin-only page (/users, /sensors, /analytics, /fields, /advisor,
  // /irrigation) · redirect to the map instead.
  // Only the ADMIN pages: Settings, messages and the other pages reached from
  // the user menu are every farmer's too. Redirecting from everything outside
  // FARMER_NAV sent a farmer opening Settings back to the map (walk 2026-10-03).
  // Admin-only routes are also guarded where they are declared (App.js).
  const ADMIN_ONLY_PATHS = ADMIN_NAV.filter(i => !i.selfGuarded).map(i => i.path)
    .filter(p => p && p !== '/' && !FARMER_NAV.some(f => f.path === p));
  React.useEffect(() => {
    if (!effectiveAdmin && ADMIN_ONLY_PATHS.some(p => location.pathname.startsWith(p))) {
      navigate('/', { replace: true });
    }
  }, [effectiveAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || /^\/fields\/\d+/.test(location.pathname);
    }
    return location.pathname.startsWith(path);
  };

  // On map: icon rail only. On other pages: full sidebar.
  const useMiniSidebar = isWideContentPage && !isSmallScreen;
  const effectiveDrawerWidth = isSmallScreen ? FULL_DRAWER_WIDTH : (useMiniSidebar ? ICON_RAIL_WIDTH : FULL_DRAWER_WIDTH);

  const adminItems = ADMIN_NAV.filter(i => !i.ent || hasEntitlement?.(i.ent));
  // In the farmer view an admin still reaches the admin pages in one press:
  // they sit under «Διαχείριση» and switch the view on the way (client
  // 2026-10-08: moving between the admin pages was too hard).
  const adminShortcuts = !isAdmin && hasAdminRole
    ? adminItems.filter(i => !FARMER_NAV.some(f => f.path === i.path)).map(i => ({ ...i, switchToAdmin: true }))
    : [];
  // The phone's fourth bottom slot: Users for an account that manages growers
  // (in either view), Alerts for a grower.
  const fourthSlot = hasAdminRole
    ? { path: '/users', label: 'usersNavLabel', icon: <People />, switchToAdmin: !isAdmin }
    : { path: '/alerts', label: 'alerts', icon: <NotificationsActive /> };

  const navItems = isAdmin
    ? adminItems
    : [...FARMER_MAIN, ...(adminShortcuts.length ? [{ section: 'admin' }, ...adminShortcuts] : [])];

  const renderNavItem = (item) => {
    const active = isActive(item.path);
    const label = t(item.label) || item.label;

    const button = (
      <ListItemButton
        key={item.path}
        selected={active}
        onClick={() => {
          if (item.switchToAdmin) setViewMode('admin');
          navigate(item.path);
          if (isSmallScreen) setMobileSidebarOpen(false);
        }}
        sx={{
          borderRadius: useMiniSidebar ? '12px' : '0 20px 20px 0',
          mx: useMiniSidebar ? 0.75 : 0.5,
          ml: useMiniSidebar ? 0.75 : 0,
          mb: 0.25,            // same in both modes · icons stay at same Y
          py: 0.75,            // same in both modes
          px: useMiniSidebar ? 0 : 1.5,
          minHeight: 40,       // same in both modes
          justifyContent: useMiniSidebar ? 'center' : 'flex-start',
          position: 'relative',
          '&::before': !useMiniSidebar ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '20%',
            bottom: '20%',
            width: 3,
            borderRadius: '0 3px 3px 0',
            bgcolor: active ? '#5a8f3c' : 'transparent',
            transition: 'background-color 0.2s',
          } : {},
          '&.Mui-selected': {
            bgcolor: useMiniSidebar ? 'rgba(90, 143, 60, 0.25)' : 'rgba(5, 150, 105, 0.12)',
            '&:hover': { bgcolor: useMiniSidebar ? 'rgba(90, 143, 60, 0.32)' : 'rgba(5, 150, 105, 0.18)' },
          },
          '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
          transition: 'all 0.15s ease',
        }}
      >
        <ListItemIcon sx={{
          color: active ? '#fff' : 'rgba(255,255,255,0.55)',
          minWidth: useMiniSidebar ? 0 : 32,
          justifyContent: 'center',
          '& .MuiSvgIcon-root': { fontSize: 22, width: 22, height: 22 },
        }}>
          {item.badgeKey === 'messages' && unreadMessages > 0 ? (
            <Badge badgeContent={unreadMessages} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}>
              {item.icon}
            </Badge>
          ) : item.icon}
        </ListItemIcon>
        {!useMiniSidebar && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontSize: '0.92rem',
              fontWeight: active ? 600 : 400,
              color: active ? '#fff' : 'rgba(255,255,255,0.65)',
              letterSpacing: '-0.01em',
            }}
          />
        )}
      </ListItemButton>
    );

    if (useMiniSidebar) {
      return (
        <Tooltip key={item.path} title={label} placement="right" arrow>
          {button}
        </Tooltip>
      );
    }
    return button;
  };

  const userInitial = (user?.first_name?.[0] || user?.username?.[0] || '?').toUpperCase();

  const drawerContent = (
    <>
      {/* Logo · same header height in both modes so the nav icons below
          stay at the same Y-coordinate when the user collapses/expands the
          sidebar. The image itself scales via objectFit: 'contain'. */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: 72,
        px: useMiniSidebar ? 0.5 : 1.5,
        py: 1,
        flexShrink: 0,
      }}>
        {!useMiniSidebar ? (
          <Box
            component="img"
            src={language === 'el' ? '/dark_gr.png' : '/dark_en.png'}
            alt="TerraMind"
            sx={{ maxWidth: '100%', maxHeight: 56, display: 'block', objectFit: 'contain' }}
          />
        ) : (
          <Box
            component="img"
            src="/dark_small.png"
            alt="TerraMind"
            sx={{ width: 44, height: 44, objectFit: 'contain' }}
          />
        )}
      </Box>

      {/* Navigation */}
      <List sx={{ px: 0, py: 0.5, flex: 1 }}>
        {navItems.map((item, idx) =>
          item.section ? (
            useMiniSidebar ? (
              <Divider key={`sec-${idx}`} sx={{ borderColor: 'rgba(255,255,255,0.12)', mx: 0.75, my: idx === 0 ? 0.5 : 1 }} />
            ) : (
              <Typography
                key={`sec-${idx}`}
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2,
                  pt: idx === 0 ? 0.5 : 1.5,
                  pb: 0.5,
                  fontSize: '0.92rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.35)',
                  userSelect: 'none',
                }}
              >
                {SECTION_LABELS[item.section]?.[language] || item.section}
              </Typography>
            )
          ) : renderNavItem(item)
        )}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {/* User section */}
      <Box sx={{ p: useMiniSidebar ? 0.75 : 1.5, pt: 0.5 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mb: 1 }} />
        {useMiniSidebar ? (
          <Tooltip title={user?.first_name || user?.username || '---'} placement="right" arrow>
            <IconButton
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              sx={{ width: '100%', borderRadius: '12px', color: 'white', py: 1 }}
            >
              <Avatar sx={{
                width: 30, height: 30, bgcolor: 'rgba(255,255,255,0.2)',
                fontSize: '0.9rem', fontWeight: 700,
              }}>
                {userInitial}
              </Avatar>
            </IconButton>
          </Tooltip>
        ) : (
          <Box
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              cursor: 'pointer', borderRadius: 2, p: 0.75, mx: -0.5,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              transition: 'background-color 0.15s',
            }}
          >
            <Avatar sx={{
              width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)',
              fontSize: '0.92rem', fontWeight: 700,
            }}>
              {userInitial}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontWeight: 600, color: 'rgba(255,255,255,0.9)',
                fontSize: '0.92rem', lineHeight: 1.3,
              }} noWrap>
                {user?.first_name || user?.username || '---'}
              </Typography>
              {/* The role only in the admin view: the simple view does not
                  label the farmer «Admin» (live walk 2026-10-02). */}
              {isAdmin && (
                <Typography sx={{
                  color: 'rgba(255,255,255,0.45)', textTransform: 'capitalize', fontSize: '0.92rem',
                }}>
                  {t('adminView') || 'Admin'}
                </Typography>
              )}
            </Box>
            <Settings sx={{ fontSize: 15, color: 'rgba(255,255,255,0.35)' }} />
          </Box>
        )}
      </Box>
    </>
  );

  const drawerPaperSx = {
    boxSizing: 'border-box',
    bgcolor: BRAND.sidebar,
    color: 'white',
    border: 'none',
    borderRadius: 0,
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <TourBanner />
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Sidebar · Desktop: persistent */}
      {!isSmallScreen && (
        <Drawer
          variant="persistent"
          open
          sx={{
            width: effectiveDrawerWidth,
            flexShrink: 0,
            transition: 'width 0.2s ease',
            '& .MuiDrawer-paper': {
              ...drawerPaperSx,
              width: effectiveDrawerWidth,
              transition: 'width 0.2s ease',
              // In the flow, not floating over it. MUI's drawer paper is
              // `position: fixed; top: 0` by default, so the sidebar was
              // painted across the top banner -- its icon and the first
              // centimetre of its text sat underneath the menu and could not
              // be read. A persistent drawer inside a flex row belongs in the
              // row.
              position: 'relative',
              height: '100%',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Sidebar · Mobile: temporary overlay */}
      {isSmallScreen && (
        <Drawer
          variant="temporary"
          open={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              ...drawerPaperSx,
              width: FULL_DRAWER_WIDTH,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Quick-create popover removed — FAB was its only anchor.
          Field creation: FieldListSidebar (+) header button.
          Note creation: map long-press or note icon in field detail. */}

      {/* User Preferences Popover */}
      <Popover
        open={Boolean(userMenuAnchor)}
        anchorEl={userMenuAnchor}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 2, minWidth: 200, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
          },
        }}
      >
        <Typography variant="caption" sx={{
          color: 'text.secondary', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: 0.5,
          mb: 1, display: 'block', fontSize: '0.92rem',
        }}>
          {t('language') || 'Language'}
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <LanguageToggle />
        </Box>
        <Divider sx={{ my: 1 }} />
        {/* F47: admin "view as farmer" toggle. Only rendered when the user
            is actually an admin. Lets admins QA the farmer experience
            without logging out into a second account. */}
        {hasAdminRole && (
          <>
            <Box sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
                {t('currentView') || 'Current view'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[
                  { mode: 'admin', label: t('adminView') || 'Admin' },
                  { mode: 'farmer', label: t('farmerView') || 'Farmer' },
                ].map(({ mode, label }) => (
                  <Box
                    key={mode}
                    onClick={() => { if (adminViewMode !== mode) toggleViewMode(); }}
                    sx={{
                      flex: 1, textAlign: 'center', py: 0.75, borderRadius: 1.5,
                      cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem',
                      bgcolor: adminViewMode === mode ? BRAND.green : 'rgba(0,0,0,0.05)',
                      color: adminViewMode === mode ? '#fff' : 'text.secondary',
                      transition: 'all 0.15s',
                      lineHeight: 1.25,
                      '&:hover': { bgcolor: adminViewMode === mode ? '#4a7c3f' : 'rgba(0,0,0,0.09)' },
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Divider sx={{ my: 1 }} />
          </>
        )}
        {[
          // The farmer pages that left the sidebar (FARMER_MORE), where the phone keeps them.
          ...(isAdmin ? [] : FARMER_MORE.map(i => ({
            icon: React.cloneElement(i.icon, { fontSize: 'small' }), label: t(i.label) || i.label,
            path: i.path, color: TEXT.secondary,
          }))),
          { icon: <Settings fontSize="small" />, label: t('settings') || 'Settings', path: '/settings', color: TEXT.secondary },
        ].map(({ icon, label, path, color }) => (
          <Box
            key={path}
            onClick={() => { setUserMenuAnchor(null); navigate(path); }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
              p: 1, borderRadius: 1.5, color,
              '&:hover': { bgcolor: `${color}10` },
              mb: 0.25,
            }}
          >
            {icon}
            <Typography sx={{ fontWeight: 600, fontSize: '0.92rem' }}>{label}</Typography>
          </Box>
        ))}
        {/* Request Help · P3.3 */}
        <Box
          onClick={() => { setUserMenuAnchor(null); setHelpOpen(true); }}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
            p: 1, borderRadius: 1.5, color: TEXT.secondary,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            mb: 0.25,
          }}
        >
          <HelpOutline fontSize="small" />
          <Typography sx={{ fontWeight: 600, fontSize: '0.92rem' }}>
            {t('requestHelp') || 'Request Help'}
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box
          onClick={() => { setUserMenuAnchor(null); logout(); }}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
            p: 1, borderRadius: 1.5, color: '#d32f2f',
            '&:hover': { bgcolor: 'rgba(211,47,47,0.08)' },
          }}
        >
          <Logout fontSize="small" />
          <Typography sx={{ fontWeight: 600, fontSize: '0.92rem' }}>
            {t('logout') || 'Logout'}
          </Typography>
        </Box>
      </Popover>

      {/* Contact / Bug Report Dialog */}
      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
          {t('contactSupport') || 'Contact Support'}
        </DialogTitle>
        <DialogContent>
          {helpSent ? (
            <Typography sx={{ color: BRAND.green, fontWeight: 700, textAlign: 'center', py: 3 }}>
              {t('helpRequestSent') || 'Message sent! We\'ll get back to you shortly.'}
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: TEXT.secondary, mb: 2, mt: 0.5 }}>
                {t('contactSupportDesc') || 'Choose a category and describe your issue.'}
              </Typography>
              {/* Type selector — professional pill buttons */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.75, mb: 2 }}>
                {HELP_TYPES.map(ht => {
                  const isSelected = helpType === ht.value;
                  return (
                    <Box key={ht.value} onClick={() => setHelpType(ht.value)} sx={{
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      px: 1.5, py: 1, borderRadius: 2, cursor: 'pointer',
                      border: `1.5px solid ${isSelected ? BRAND.green : SURFACE.border}`,
                      bgcolor: isSelected ? `${BRAND.green}10` : '#fff',
                      transition: 'all 0.12s',
                      '&:hover': { border: `1.5px solid ${BRAND.green}`, bgcolor: `${BRAND.green}08` },
                    }}>
                      <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>{ht.emoji}</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? BRAND.green : TEXT.secondary }}>
                        {t(ht.labelKey) || ht.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <TextField
                multiline rows={4} fullWidth autoFocus
                placeholder={helpType === 'bug'
                  ? (t('bugReportPlaceholder') || 'Describe what happened, what you expected, and your browser/device...')
                  : (t('requestHelpPlaceholder') || 'Describe what you need help with...')}
                value={helpMsg}
                onChange={(e) => setHelpMsg(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </>
          )}
        </DialogContent>
        {!helpSent && (
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setHelpOpen(false)} sx={{ color: TEXT.secondary }}>
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              variant="contained"
              onClick={sendHelp}
              disabled={!helpMsg.trim() || helpSending}
              startIcon={helpSending ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ bgcolor: BRAND.green, '&:hover': { bgcolor: '#4a7c3f' }, borderRadius: 2, fontWeight: 700 }}
            >
              {helpSending ? '...' : (t('send') || 'Send')}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Main Content */}
      <Box sx={{
        flexGrow: 1, minWidth: 0,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        // No height: '100vh' here · flexGrow: 1 already fills the remaining
        // space inside the outer 100vh container.  An explicit 100vh on a
        // flex-child ignores the topbar offset, overflowing by ~44-48px and
        // creating an unwanted body scrollbar on every page.
      }}>
        {/* Top Bar · minimal for map, standard for other pages */}
        <Paper
          sx={{
            px: 2, py: 0,
            minHeight: isMapView ? 44 : 48,
            bgcolor: '#ffffff',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            borderRadius: 0,
            boxShadow: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isSmallScreen && (
              <IconButton onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)} size="small" aria-label={t('ariaToggleNav')}>
                <MenuIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
            {/* Page titles now handled by PageHeader inside each page · top bar is utility-only */}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 5 }}>
            {topBarRight}
            {/*
              Language toggle: F1 · previously wrapped in `display: contents`,
              which removed its box and let NotificationBell's badge spill on
              top. Each top-bar pill now owns a real Box with its own stacking
              order so the badge can't visually swallow the language switcher.
            */}
            <Box
              data-tour="language-toggle"
              sx={{ position: 'relative', zIndex: 3, display: 'flex' }}
            >
              <LanguageToggle compact />
            </Box>
            <Box
              data-tour="notification-bell"
              sx={{ position: 'relative', zIndex: 2, display: 'flex' }}
            >
              <NotificationBell />
            </Box>
            {/* WeatherWidget is a tap target to /weather on mobile · no separate nav item needed */}
            <Box
              onClick={() => navigate('/weather')}
              sx={{ cursor: 'pointer', display: 'flex' }}
              role="link"
              aria-label={t('weather')}
            >
              <WeatherWidget compact />
            </Box>
          </Box>
        </Paper>

        {/* Content Area */}
        <Box sx={{
          flexGrow: 1,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: isMapView ? 'transparent' : BRAND.cream,
          minWidth: 0,
          // Reserve space for the bottom nav bar on small screens
          pb: isSmallScreen ? 'calc(56px + env(safe-area-inset-bottom))' : 0,
        }}>
          {children}
        </Box>

        {/* Bottom Navigation · xs/sm only.
            FAB removed — it was overlapping map controls and content on narrow
            screens. "Add field" is accessible via the empty-state screen or
            the field list header. */}
        {isSmallScreen && (
          <Paper
            elevation={3}
            sx={{
              position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200,
              borderTop: '1px solid rgba(0,0,0,0.08)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            {/* Mobile bottom nav: 5 real slots, no hidden items.
                Fields (/, the home list) · Schedule · Reports · fourth · Profile.
                The fourth slot (audit step 4, George 16/10/2026): Alerts for a
                grower, the screen the alert email and the bell send him to;
                Users for an account that manages growers, so adding one is
                always a tap away («always visible»). Alerts keep the bell. */}
            <BottomNavigation
              value={
                // Home is the field list, so a field's own page (/fields/12) is Home too.
                (location.pathname === '/' || location.pathname.startsWith('/fields')) ? 0
                : location.pathname.startsWith('/schedules') ? 1
                : location.pathname.startsWith('/reports') ? 2
                : location.pathname.startsWith(fourthSlot.path) ? 3
                : false
              }
              onChange={(_, val) => {
                const routes = ['/', '/schedules', '/reports', fourthSlot.path];
                if (val === 3 && fourthSlot.switchToAdmin) setViewMode('admin');
                if (routes[val] != null) navigate(routes[val]);
              }}
              showLabels
              sx={{
                bgcolor: '#fff', height: 56,
                // Five labels in 390 px: at the default size «Προγράμματα» and
                // «Ημερολόγιο» ran into each other (live walk 2026-10-02). Whole
                // words, never truncated, at a size that fits a fifth each.
                '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.25 },
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.66rem', lineHeight: 1.15, whiteSpace: 'nowrap',
                  '&.Mui-selected': { fontSize: '0.7rem' },
                },
              }}
            >
              <BottomNavigationAction label={t('myFields') || 'Fields'} icon={<Landscape />} />
              <BottomNavigationAction label={t('weeklySchedules') || 'Schedule'} icon={<CalendarMonth />} />
              <BottomNavigationAction label={t('reportsNavLabel') || 'Reports'} icon={<DescriptionIcon />} />
              <BottomNavigationAction label={t(fourthSlot.label)} icon={fourthSlot.icon} />
              <BottomNavigationAction
                label={t('profile') || 'Profile'}
                icon={<Person />}
                onClick={(e) => {
                  e.preventDefault();
                  setUserMenuAnchor(e.currentTarget);
                }}
              />
            </BottomNavigation>
          </Paper>
        )}
      </Box>
      </Box>
      <InstallBanner />
    </Box>
  );
};

export default PageLayout;
